// src/pages/PaymentSuccessPage.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Home, Loader, XCircle } from 'lucide-react';
import api from '../api/client';

const PaymentSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'checking' | 'success' | 'timeout'>('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 15; // 15 сек (15 × 1)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const checkPaid = async () => {
      try {
        const res = await api.get('/auth/me');
        const user = res.data?.user || res.data;
        if (user?.paid) {
          setStatus('success');
          if (intervalRef.current) clearInterval(intervalRef.current);
          setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
        }
      } catch (e) {
        // игнорируем, пробуем снова
      }
    };

    checkPaid();

    intervalRef.current = setInterval(() => {
      setAttempts(prev => {
        const next = prev + 1;
        if (next >= maxAttempts) {
          setStatus('timeout');
          if (intervalRef.current) clearInterval(intervalRef.current);
          return next;
        }
        return next;
      });
      checkPaid();
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [navigate]);

  return (
    <div style={styles.container}>
      <div style={styles.bgBlur1} />
      <div style={styles.bgBlur2} />

      <div style={styles.card}>
        {status === 'checking' && (
          <>
            <div style={styles.spinnerWrapper}>
              <div style={styles.spinnerRing} />
              <Loader size={32} color="#6a11cb" style={styles.spinnerIcon} />
            </div>
            <h1 style={styles.title}>Проверяем оплату…</h1>
            <p style={styles.subtitle}>
              Если вы оплатили, доступ активируется в течении минуты.
            </p>
            <div style={styles.progress}>
              <div style={{ ...styles.progressBar, width: `${Math.min((attempts / maxAttempts) * 100, 95)}%` }} />
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={styles.iconWrapper}>
              <CheckCircle size={64} color="#22c55e" />
            </div>
            <h1 style={styles.titleSuccess}>Доступ активирован!</h1>
            <p style={styles.subtitle}>Перенаправляем в личный кабинет…</p>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div style={styles.iconWrapper}>
              <XCircle size={64} color="#f59e0b" />
            </div>
            <h1 style={styles.title}>Оплата не завершена</h1>
            <p style={styles.subtitle}>
              Если вы отменили оплату, просто вернитесь в личный кабинет.
              Если оплата была совершена — напишите на{' '}
              <a href="mailto:info@hrlama.ru" style={styles.link}>info@hrlama.ru</a>.
            </p>
            <div style={styles.buttons}>
              <button style={styles.primaryButton} onClick={() => navigate('/dashboard', { replace: true })}>
                <Home size={18} style={{ marginRight: 8 }} />
                Вернуться в личный кабинет
              </button>
              <button style={styles.secondaryButton} onClick={() => window.location.reload()}>
                Проверить снова
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== СТИЛИ ====================
const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(145deg, #f8fafd 0%, #ede9fe 100%)',
    padding: '20px',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
  },
  bgBlur1: {
    position: 'absolute',
    top: '-10%',
    left: '-5%',
    width: '600px',
    height: '600px',
    background: 'radial-gradient(circle, rgba(168,85,247,0.18) 0%, rgba(168,85,247,0) 70%)',
    borderRadius: '50%',
    filter: 'blur(80px)',
    zIndex: 0,
  },
  bgBlur2: {
    position: 'absolute',
    bottom: '-10%',
    right: '-5%',
    width: '700px',
    height: '700px',
    background: 'radial-gradient(circle, rgba(37,117,252,0.18) 0%, rgba(37,117,252,0) 70%)',
    borderRadius: '50%',
    filter: 'blur(80px)',
    zIndex: 0,
  },
  card: {
    position: 'relative',
    zIndex: 1,
    background: 'rgba(255,255,255,0.65)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: 32,
    padding: '48px 40px',
    maxWidth: 520,
    width: '100%',
    textAlign: 'center',
    border: '1px solid rgba(255,255,255,0.7)',
    boxShadow: '0 25px 60px rgba(106,17,203,0.15), 0 0 40px rgba(37,117,252,0.08)',
  },
  spinnerWrapper: {
    position: 'relative',
    width: 80,
    height: 80,
    margin: '0 auto 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinnerRing: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: '50%',
    border: '4px solid transparent',
    borderTopColor: '#6a11cb',
    borderRightColor: '#2575fc',
    borderBottomColor: '#6a11cb',
    borderLeftColor: '#2575fc',
    animation: 'spin 1.2s linear infinite',
    boxShadow: '0 0 25px rgba(37,117,252,0.5)',
  },
  spinnerIcon: {
    zIndex: 1,
    animation: 'spin 2s linear infinite',
  },
  iconWrapper: {
    margin: '0 auto 24px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    filter: 'drop-shadow(0 8px 24px rgba(106,17,203,0.2))',
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    color: '#1a1a2e',
    margin: '0 0 12px',
  },
  titleSuccess: {
    fontSize: 26,
    fontWeight: 700,
    color: '#22c55e',
    margin: '0 0 12px',
  },
  subtitle: {
    fontSize: 15,
    color: '#4a4a68',
    lineHeight: 1.5,
    margin: '0 0 20px',
  },
  progress: {
    width: '100%',
    height: 6,
    background: 'rgba(106,17,203,0.1)',
    borderRadius: 3,
    overflow: 'hidden',
    marginTop: 16,
  },
  progressBar: {
    height: '100%',
    background: 'linear-gradient(90deg, #6a11cb 0%, #2575fc 100%)',
    borderRadius: 3,
    transition: 'width 0.3s ease',
  },
  buttons: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginTop: 24,
  },
  primaryButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 28px',
    borderRadius: 40,
    border: 'none',
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 8px 20px rgba(106,17,203,0.35)',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  secondaryButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px 28px',
    borderRadius: 40,
    border: '1px solid rgba(37,117,252,0.4)',
    background: 'rgba(255,255,255,0.6)',
    color: '#2575fc',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  link: {
    color: '#2575fc',
    textDecoration: 'none',
    fontWeight: 600,
  },
};

export default PaymentSuccessPage;
